import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { Database } from '../../types/database.types';
import type { SalesTaskRow } from './salesTasksLens';

/**
 * Reading and writing the Sales Tasks page.
 *
 * The read is one view, `sales_tasks_home` (20270146000100), which already
 * carries the assignee's name and the name of whichever record the task is
 * about. That is deliberate: a list of forty tasks would otherwise be forty
 * lookups, or a join written twice — once here and once in whatever screen
 * asks the same question next.
 *
 * The writes go to `public.tasks` directly, because a view is not a write
 * surface and because the task that gets ticked here is the same row My Day,
 * My Tasks and the project board hold. There is one task table now, which is
 * the whole point of the drop this page sits on.
 */

const PAGE_SIZE = 2000;

/**
 * Drop 230. Supabase clamps every response to its server max_rows, 1000 by
 * default, silently. The association picker read each table in one request,
 * so past 1000 prospects the older ones could not be picked. Every list here
 * now pages, advancing by what each page actually returned, stopping on the
 * first empty page, and failing loudly rather than truncating.
 */
export const ASSOCIATION_PAGE = 1000;
const ASSOCIATION_PAGE_LIMIT = 100;

interface PageResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

async function readEveryRow<T>(
  table: string,
  page: (from: number, to: number) => PromiseLike<PageResult<T>>,
): Promise<T[]> {
  const everyone: T[] = [];
  for (let n = 0; n < ASSOCIATION_PAGE_LIMIT; n += 1) {
    const from = everyone.length;
    const { data, error } = await page(from, from + ASSOCIATION_PAGE - 1);
    if (error) fail(error.message);
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0) return everyone;
    everyone.push(...rows);
  }
  fail(`More than ${String(ASSOCIATION_PAGE * ASSOCIATION_PAGE_LIMIT)} ${table}; paging stopped.`);
}

function fail(message: string): never {
  throw new Error(message);
}

export const salesTaskKeys = {
  all: () => ['sales-tasks'] as const,
  associations: (dealId: string | null) => ['sales-tasks', 'associations', dealId] as const,
};

export interface SalesTaskAssociationOption {
  id: string;
  label: string;
}

export interface SalesTaskLinkedDeal {
  id: string;
  name: string;
  hubspotUrl: string | null;
}

export interface SalesTaskAssociations {
  leads: SalesTaskAssociationOption[];
  companies: SalesTaskAssociationOption[];
  deals: SalesTaskLinkedDeal[];
  prospects: SalesTaskAssociationOption[];
  linkedDeal: SalesTaskLinkedDeal | null;
}

export async function fetchSalesTasks(client: SupabaseClient): Promise<SalesTaskRow[]> {
  const { data, error } = await client
    .from('sales_tasks_home')
    .select('*')
    // Ordered again in the lens, because the page's rule (undated last,
    // overdue above today) is not expressible here and must not live in two
    // places. This order only makes the payload deterministic.
    .order('due_date', { ascending: true, nullsFirst: false })
    .limit(PAGE_SIZE);
  if (error) fail(error.message);
  return Array.isArray(data) ? data : [];
}

export function useSalesTasks() {
  const { client } = useAuth();
  return useQuery({
    queryKey: salesTaskKeys.all(),
    queryFn: () => fetchSalesTasks(client),
    retry: false,
  });
}

/**
 * The choices shown only while one task is open, plus the linked deal's
 * HubSpot address. Keeping this outside `sales_tasks_home` avoids widening a
 * global view for one dialog-only link.
 */
export async function fetchSalesTaskAssociations(
  client: SupabaseClient,
  dealId: string | null,
): Promise<SalesTaskAssociations> {
  // The id order breaks ties, so a name shared across a page boundary can
  // neither repeat nor go missing.
  const [leadRows, companyRows, dealRows, prospectRows] = await Promise.all([
    readEveryRow('leads', (from, to) =>
      client
        .from('leads')
        .select('id, first_name, last_name, email')
        .order('first_name')
        .order('id')
        .range(from, to),
    ),
    readEveryRow('companies', (from, to) =>
      client.from('companies').select('id, name').order('name').order('id').range(from, to),
    ),
    readEveryRow('deals', (from, to) =>
      client
        .from('deals')
        .select('id, name, hubspot_url')
        .order('name')
        .order('id')
        .range(from, to),
    ),
    readEveryRow('prospects', (from, to) =>
      client
        .from('prospects')
        .select('id, first_name, last_name, email')
        .order('first_name')
        .order('id')
        .range(from, to),
    ),
  ]);

  const leads = leadRows.map((lead) => {
    const name = `${lead.first_name} ${lead.last_name}`.trim();
    return { id: lead.id, label: name === '' ? lead.email : name };
  });
  const companies = companyRows.map((company) => ({ id: company.id, label: company.name }));
  const deals = dealRows.map((deal) => ({
    id: deal.id,
    name: deal.name,
    hubspotUrl: deal.hubspot_url,
  }));
  const prospects = prospectRows.map((prospect) => {
    const name = `${prospect.first_name} ${prospect.last_name}`.trim();
    return { id: prospect.id, label: name === '' ? prospect.email : name };
  });

  return {
    leads,
    companies,
    deals,
    prospects,
    linkedDeal: deals.find((deal) => deal.id === dealId) ?? null,
  };
}

export function useSalesTaskAssociations(dealId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: salesTaskKeys.associations(dealId),
    queryFn: () => fetchSalesTaskAssociations(client, dealId),
    retry: false,
  });
}

/**
 * Tick a task, or untick it.
 *
 * `completed_at` is left alone on purpose. The `tasks_maintain_timestamps`
 * trigger owns it, and a browser that sent its own value would be telling the
 * database when something happened based on a clock it does not control.
 *
 * Anybody may complete anybody's task — Kyu's ruling, 14 September — and the
 * owner is told automatically, by the `task_completed` notification the same
 * migration added. Completing your own is silent, because enqueue_notification
 * drops the actor from its own audience.
 */
export function useSetSalesTaskCompleted() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskId: string; isCompleted: boolean }): Promise<void> => {
      const { error } = await client
        .from('tasks')
        .update({ is_completed: input.isCompleted })
        .eq('id', input.taskId);
      if (error) fail(error.message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: salesTaskKeys.all() });
    },
  });
}

/**
 * The fields the dialog may change.
 *
 * Deliberately the same shape `useUpdateDealTask` already uses for deal tasks,
 * because after 20270146000100 they are the same row in the same table. Two
 * different edit surfaces for one row is how the two task tables started.
 *
 * `is_completed` is not here: it has its own hook above, because the tick in
 * the table has to work without opening anything.
 */
export type SalesTaskChanges = Partial<{
  title: string;
  task_type: Database['public']['Enums']['task_type'];
  assignee_id: string | null;
  due_date: string | null;
  // These are tags, not parents. 20270124000100 grants both to authenticated
  // callers. deal_id stays absent because the guarded reparent RPC owns it.
  lead_id: string | null;
  company_id: string | null;
}>;

/**
 * Save an edit from the dialog. Only the keys supplied are written.
 *
 * Aimed at `tasks`, not at `sales_tasks_home`: a view is not a write surface,
 * and the row being edited is the same one My Day and the project board hold.
 */
export function useUpdateSalesTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskId: string; changes: SalesTaskChanges }): Promise<void> => {
      const { error } = await client.from('tasks').update(input.changes).eq('id', input.taskId);
      if (error) fail(error.message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: salesTaskKeys.all() });
      // The same row shows on the Deals tasks tab and in My Tasks. Leaving
      // those stale is how somebody ends up reading a due date that changed
      // ten minutes ago on another screen.
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * Change the security parent of a Sales Task through the guarded database
 * door. Clearing the deal moves it back to the one Sales follow-ups holder;
 * setting a deal moves it out of that project. The RPC checks edit authority
 * under both parents before it changes either id.
 */
export function useSetSalesTaskDeal() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskId: string; dealId: string | null }): Promise<void> => {
      const args =
        input.dealId === null
          ? { p_task: input.taskId }
          : { p_task: input.taskId, p_deal: input.dealId };
      const { error } = await client.rpc('set_sales_task_deal', args);
      if (error) fail(error.message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: salesTaskKeys.all() });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/** Complete one task and create its dated successor in the same transaction. */
export function useRecycleSalesTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      title: string;
      dueDate: string;
    }): Promise<string> => {
      const { data, error } = await client.rpc('recycle_sales_task', {
        p_task: input.taskId,
        p_title: input.title,
        p_due_date: input.dueDate,
      });
      if (error) fail(error.message);
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: salesTaskKeys.all() });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * What a failed write means in words.
 *
 * Row Level Security refuses by returning no rows rather than by explaining
 * itself, so a bare Postgres message here reads as a fault in the page. The
 * one case worth naming is the sales-member gate, since that is the refusal a
 * real person will actually meet.
 */
export function salesTaskErrorMessage(cause: unknown): string {
  const raw = cause instanceof Error ? cause.message : '';
  if (
    raw.includes('cannot_edit_current_task_parent') ||
    raw.includes('cannot_edit_new_task_parent')
  ) {
    return 'You cannot change this task under one of those records.';
  }
  if (raw.includes('sales_task_already_completed')) {
    return 'This task was already completed and cannot be recycled again.';
  }
  if (raw.includes('not_a_sales_member') || raw.includes('row-level security')) {
    return 'That task belongs to the sales tracker and your account cannot change it.';
  }
  return raw === '' ? 'That did not save. Try again in a moment.' : raw;
}
